/**
 * BiddingPanel — opens during `phase === 'bidding' && !bidding.resolved`.
 * Owner: A3.2. RULES §4.2 v1.2 (Phase 12 — timer + explicit pass).
 *
 * The panel has FOUR observable modes, switched on
 * `(openingResolved, you === callerSeat, you === highBidder)`:
 *
 *   1. opening + caller   — "Your turn to call trump" hero, 4 trump buttons,
 *                           single "Bid" button (forced base bid value).
 *                           **No countdown** — caller takes as long as they
 *                           want. (v1.2 owner-verified: Mode 1 must never
 *                           render the auction countdown.)
 *
 *   2. opening + others   — "Waiting for {caller} to call trump…" banner.
 *                           No input. No countdown.
 *
 *   3. open    + bidder   — countdown timer + current high bid + value picker
 *                           + 4 trump buttons + "Raise" button + (v1.2)
 *                           **"Pass" button**. If the local player has
 *                           already passed this auction window, the raise
 *                           form is replaced by a "You have passed…" banner.
 *
 *   4. open    + locked   — (you are the high bidder) countdown timer +
 *                           "You hold the high bid: N {trump}". No input.
 *
 * Whenever any seat has passed in this auction window (Modes 3 and 4 only),
 * a passed-pill row renders below the countdown, listing each passed seat
 * by display name. v1.2-new.
 *
 * The server is the bidding oracle (RULES §4.2; engine `bidding.ts`).
 * Submission is debounced for 250 ms to defend against double-send. The
 * server already rejects out-of-turn, but the UX is cleaner.
 *
 * Countdown derivation [RULES §4.2.1 informative]: `view.bidding.auctionEndsAt`
 * is the server's wall-clock deadline (Unix ms). v1.2 tightened the server
 * constant from 15_000 → 10_000 ms; the client just reads the deadline
 * verbatim. We render seconds-with-one-decimal counting down via a 100 ms
 * setInterval. We DO NOT use the local clock for state — when the visual
 * reaches 0.0 s we hold there until the server's `endAuction` push arrives
 * and the phase transitions (or every non-high-bidder passes and the engine
 * resolves synchronously inside `applyMove`).
 *
 * Test contract (P5-Tests):
 *   - `.bidding-panel[data-mode="opening"]` — opening sub-phase
 *   - `.bidding-panel[data-mode="overbid"]` — open sub-phase
 *   - `.bidding-panel-submit` — the action button (bid or raise)
 *   - `.bidding-panel-pass` — v1.2 pass button (Mode 3 only)
 *   - `.bidding-panel-passed-pill` — passed-seat pill (Modes 3+4)
 *   - `[aria-live="polite"]` on the countdown
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Suit } from "@hookum/protocol";
import { useLobby, useView } from "../state/hooks.js";
import { actions } from "../wire/index.js";

const SUITS: Suit[] = ["C", "D", "H", "S"];
const SUIT_GLYPH: Record<Suit, string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
};
const SUIT_NAME: Record<Suit, string> = {
  C: "Clubs",
  D: "Diamonds",
  H: "Hearts",
  S: "Spades",
};

/** Re-render at ~10 Hz so the countdown digit ticks visibly. We never use
 * `now` for state decisions — only for the cosmetic countdown render. */
function useTicker(activeMs: number): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (activeMs <= 0) return;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [activeMs]);
  return now;
}

export function BiddingPanel(): JSX.Element | null {
  const view = useView();
  const lobby = useLobby();

  const auctionEndsAt = view?.bidding.auctionEndsAt ?? null;
  // Trigger ticker whenever there's a live deadline (open phase). The dep
  // is the deadline itself so a fresh bid (new deadline) restarts the
  // interval cleanly.
  const now = useTicker(auctionEndsAt ?? 0);

  // ---- always-on hooks above; gating below ----

  const visible = Boolean(
    view && view.phase === "bidding" && !view.bidding.resolved,
  );

  const isCaller = view ? view.you === view.callerSeat : false;
  const openingResolved = view ? view.bidding.openingResolved : false;
  const isHighBidder = view
    ? view.bidding.highBidder !== null && view.you === view.bidding.highBidder
    : false;
  const highBid = view ? view.bidding.highBid : null;
  // v1.2: per-seat permanent pass flags. Defensive default for older fixtures
  // that don't carry the field (the field is always present in v1.2 wire).
  const passedFlags: boolean[] = view?.bidding.passed ?? [];
  const youPassed = view ? passedFlags[view.you] === true : false;
  // v1.4 Phase 20 (bidding skip): high-bidder consent flag + ready counter.
  // Counter = passed.count + (highBidderReady ? 1 : 0). When counter ===
  // config.players, the engine has already chained to applyEndAuction on
  // the same applyMove that flipped the last bit; the bidding-resolved
  // broadcast will land milliseconds later.
  const highBidderReady = view?.bidding.highBidderReady ?? false;
  const playerCount = view?.config.players ?? 4;
  const passedCount = passedFlags.filter((p) => p === true).length;
  const readyCount = passedCount + (highBidderReady ? 1 : 0);

  // Trump selection — defaults to clubs for the caller's opening; resets on
  // mount-style cycle if the bidding identity changes (e.g. another seat
  // takes the high bid and our role shifts).
  const [chosenTrump, setChosenTrump] = useState<Suit>("C");
  // For the overbid form, default value to `highBid.value + 1`.
  const minOverbid = highBid ? highBid.value + 1 : view?.rc.baseBid ?? 1;
  const maxOverbid = view?.rc.totalTricks ?? 13;
  const [chosenValue, setChosenValue] = useState<number>(minOverbid);
  const [locked, setLocked] = useState(false);

  // Snap chosenValue back into the legal range whenever the high bid changes
  // (a competing bid landed). The lower bound is the only thing that matters
  // — chosenValue must always be ≥ minOverbid.
  useEffect(() => {
    setLocked(false);
    setChosenValue((cur) => {
      if (cur < minOverbid) return minOverbid;
      if (cur > maxOverbid) return maxOverbid;
      return cur;
    });
  }, [minOverbid, maxOverbid]);

  // Caller's name — derived from lobby (defensive fallback like HUD.tsx).
  const callerName = useMemo<string>(() => {
    if (!view) return "the caller";
    return (
      lobby?.seats.find((s) => s.seatIndex === view.callerSeat)?.displayName ??
      `seat ${view.callerSeat}`
    );
  }, [lobby, view]);

  const highBidderName = useMemo<string | null>(() => {
    if (!view || view.bidding.highBidder === null) return null;
    if (view.you === view.bidding.highBidder) return "you";
    return (
      lobby?.seats.find((s) => s.seatIndex === view.bidding.highBidder)
        ?.displayName ?? `seat ${view.bidding.highBidder}`
    );
  }, [lobby, view]);

  const onSubmitOpening = useCallback((): void => {
    if (locked || !view) return;
    setLocked(true);
    actions.bid(view.rc.baseBid, chosenTrump);
    window.setTimeout(() => setLocked(false), 250);
  }, [locked, view, chosenTrump]);

  const onSubmitRaise = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault();
      if (locked || !view) return;
      if (chosenValue < minOverbid || chosenValue > maxOverbid) return;
      setLocked(true);
      actions.bid(chosenValue, chosenTrump);
      window.setTimeout(() => setLocked(false), 250);
    },
    [locked, view, chosenValue, chosenTrump, minOverbid, maxOverbid],
  );

  // v1.2 (RULES §4.2): explicit pass. Sends `{ t: "pass" }` once; the engine
  // is the oracle for whether the local seat is allowed to pass. Debounced
  // identically to bid submissions.
  const onSubmitPass = useCallback((): void => {
    if (locked || !view) return;
    setLocked(true);
    actions.pass();
    window.setTimeout(() => setLocked(false), 250);
  }, [locked, view]);

  // v1.4 Phase 20 (bidding skip): high-bidder toggles their ready-to-end flag.
  // Server rejects unless the local seat is the current high bidder and the
  // open sub-phase is active; the engine is the oracle. Debounced identically.
  const onSubmitToggleReady = useCallback((): void => {
    if (locked || !view) return;
    setLocked(true);
    actions.toggleReady();
    window.setTimeout(() => setLocked(false), 250);
  }, [locked, view]);

  if (!view || !visible) return null;

  // Countdown rendering — seconds with one decimal, clamped at 0.0. Held at
  // 0.0 if the local clock has passed the deadline (server's endAuction push
  // will fire shortly and transition the phase).
  const secondsLeft =
    auctionEndsAt !== null ? Math.max(0, (auctionEndsAt - now) / 1000) : null;
  const countdownLabel =
    secondsLeft !== null ? `${secondsLeft.toFixed(1)} s left` : null;

  // -----------------------------------------------------------------------
  // Mode 1+2: opening sub-phase
  // -----------------------------------------------------------------------
  if (!openingResolved) {
    if (!isCaller) {
      // Mode 2: spectating during opening.
      return (
        <section
          className="bidding-panel bidding-panel-waiting"
          role="region"
          aria-labelledby="bidding-panel-title"
          data-mode="opening"
        >
          <header className="bidding-panel-header">
            <h2 id="bidding-panel-title" className="bidding-panel-title">
              Bidding
            </h2>
            <p className="bidding-panel-sub" aria-live="polite">
              Waiting for <strong>{callerName}</strong> to call trump…
            </p>
          </header>
        </section>
      );
    }

    // Mode 1: caller, picking trump.
    return (
      <section
        className="bidding-panel bidding-panel-caller"
        role="region"
        aria-labelledby="bidding-panel-title"
        data-mode="opening"
      >
        <header className="bidding-panel-header bidding-panel-hero">
          <h2 id="bidding-panel-title" className="bidding-panel-title bidding-panel-title-hero">
            Your turn to call trump
          </h2>
          <p className="bidding-panel-sub">
            Pick a trump suit. Your bid is locked at{" "}
            <strong>{view.rc.baseBid}</strong>.
          </p>
        </header>

        <div
          className="bidding-panel-row bidding-panel-trumps"
          role="radiogroup"
          aria-label="Trump suit"
        >
          {SUITS.map((s) => {
            const selected = chosenTrump === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`Trump ${SUIT_NAME[s]}`}
                disabled={locked}
                className={
                  "bidding-panel-trump" +
                  ` bidding-panel-trump-${s.toLowerCase()}` +
                  (selected ? " bidding-panel-trump-selected" : "")
                }
                onClick={() => setChosenTrump(s)}
              >
                <span className="bidding-panel-trump-glyph" aria-hidden="true">
                  {SUIT_GLYPH[s]}
                </span>
                <span className="bidding-panel-trump-letter" aria-hidden="true">
                  {s}
                </span>
              </button>
            );
          })}
        </div>

        <div className="bidding-panel-actions">
          <button
            type="button"
            className="bidding-panel-submit"
            disabled={locked}
            onClick={onSubmitOpening}
            aria-label={`Bid ${view.rc.baseBid} ${SUIT_NAME[chosenTrump]}`}
          >
            Bid {view.rc.baseBid} <span aria-hidden="true">{SUIT_GLYPH[chosenTrump]}</span>
          </button>
        </div>
      </section>
    );
  }

  // -----------------------------------------------------------------------
  // Mode 3+4: open sub-phase (auction is live)
  // -----------------------------------------------------------------------
  const highBidLine =
    highBid && highBidderName ? (
      <p className="bidding-panel-high">
        <strong>{highBidderName === "you" ? "You" : highBidderName}</strong>{" "}
        {highBidderName === "you" ? "hold" : "holds"} the high bid:{" "}
        <span className="bidding-panel-high-value">{highBid.value}</span>{" "}
        <span
          className={
            "bidding-panel-high-suit suit-" +
            (highBid.trump === "D" || highBid.trump === "H" ? "red" : "black")
          }
          aria-label={SUIT_NAME[highBid.trump]}
        >
          <span aria-hidden="true">{SUIT_GLYPH[highBid.trump]}</span>
          <span className="bidding-panel-suit-letter" aria-hidden="true">
            {highBid.trump}
          </span>
        </span>
      </p>
    ) : null;

  const countdown = (
    <div className="bidding-panel-countdown" aria-live="polite">
      <span className="bidding-panel-countdown-label">Auction ends in</span>
      <span
        className={
          "bidding-panel-countdown-value" +
          (secondsLeft !== null && secondsLeft <= 5
            ? " bidding-panel-countdown-urgent"
            : "")
        }
        data-testid="bidding-countdown"
      >
        {countdownLabel ?? "—"}
      </span>
    </div>
  );

  // v1.4 Phase 20 (bidding skip): "X / N ready" counter shown in Modes 3 + 4.
  // Updates live as each seat passes / un-passes (and as the high-bidder
  // toggles ready). When N/N, the engine ends the auction synchronously —
  // the counter will briefly read N/N before the bidding-resolved broadcast.
  const readyCounter = (
    <div
      className={
        "bidding-panel-ready-counter" +
        (readyCount === playerCount
          ? " bidding-panel-ready-counter-complete"
          : "")
      }
      aria-live="polite"
      data-testid="bidding-ready-counter"
    >
      <span className="bidding-panel-ready-label">Ready to start:</span>
      <span className="bidding-panel-ready-value">
        {readyCount} / {playerCount}
      </span>
    </div>
  );

  // v1.2 — passed-pill row, shown in Modes 3 + 4 whenever ≥ 1 seat has passed.
  const passedPills = (() => {
    const seats: { seatIndex: number; displayName: string }[] = [];
    for (let s = 0; s < passedFlags.length; s++) {
      if (passedFlags[s] !== true) continue;
      const name =
        lobby?.seats.find((x) => x.seatIndex === s)?.displayName ?? `Seat ${s + 1}`;
      seats.push({ seatIndex: s, displayName: name });
    }
    if (seats.length === 0) return null;
    return (
      <div
        className="bidding-panel-passed-list"
        role="list"
        aria-label="Seats that have passed this auction"
      >
        <span className="bidding-panel-passed-label">Passed:</span>
        {seats.map((s) => (
          <span
            key={s.seatIndex}
            role="listitem"
            className="bidding-panel-passed-pill"
            data-seat={s.seatIndex}
          >
            {s.displayName}
            <span className="bidding-panel-passed-pill-tag">Passed</span>
          </span>
        ))}
      </div>
    );
  })();

  if (isHighBidder) {
    // Mode 4: high bidder. Can't raise (own bid) or pass (holding contract),
    // but v1.4 Phase 20 gives a "Skip auction" toggle: signal ready-to-end
    // and the auction ends synchronously once every non-high-bidder has
    // also passed. Otherwise the 10-s timer still works as the fallback.
    return (
      <section
        className="bidding-panel bidding-panel-locked"
        role="region"
        aria-labelledby="bidding-panel-title"
        data-mode="overbid"
      >
        <header className="bidding-panel-header">
          <h2 id="bidding-panel-title" className="bidding-panel-title">
            Bidding open
          </h2>
          {highBidLine}
          <p className="bidding-panel-sub">
            {highBidderReady
              ? "Ready — waiting for the rest…"
              : "Others can still raise…"}
          </p>
        </header>
        {countdown}
        {readyCounter}
        {passedPills}
        <div className="bidding-panel-actions">
          <button
            type="button"
            className={
              "bidding-panel-skip" +
              (highBidderReady ? " bidding-panel-skip-active" : "")
            }
            disabled={locked}
            onClick={onSubmitToggleReady}
            aria-pressed={highBidderReady}
            aria-label={
              highBidderReady
                ? "Cancel ready — keep waiting for raises"
                : "Skip the 10-second timer — end auction once everyone is ready"
            }
            data-testid="bidding-skip-button"
          >
            {highBidderReady ? "✓ Ready" : "Skip auction"}
          </button>
        </div>
      </section>
    );
  }

  // Mode 3: overbidder.
  // If the engine somehow doesn't give us a legal step (e.g. high bid already
  // at T), render the panel as a watch-only view with no raise form. This
  // matches the engine's empty `legalMoves` semantics in that edge case.
  const canRaise = highBid !== null && minOverbid <= maxOverbid;

  return (
    <section
      className="bidding-panel bidding-panel-overbid"
      role="region"
      aria-labelledby="bidding-panel-title"
      data-mode="overbid"
    >
      <header className="bidding-panel-header">
        <h2 id="bidding-panel-title" className="bidding-panel-title">
          Bidding open — raise, pass, or wait
        </h2>
        {highBidLine}
      </header>

      {countdown}
      {readyCounter}
      {passedPills}

      {youPassed ? (
        // v1.2: the local player has already passed this auction. The engine
        // would reject any further bid/pass from us with ALREADY_PASSED, so
        // we hide the form entirely and show a positive-state banner.
        <p className="bidding-panel-passed-self" aria-live="polite">
          <strong>You have passed.</strong> Waiting for the auction to end…
        </p>
      ) : canRaise ? (
        <form className="bidding-panel-form" onSubmit={onSubmitRaise}>
          <div className="bidding-panel-row">
            <label className="bidding-panel-label" htmlFor="bid-value">
              Raise to
            </label>
            <input
              id="bid-value"
              type="range"
              min={minOverbid}
              max={maxOverbid}
              step={1}
              value={chosenValue}
              disabled={locked || minOverbid === maxOverbid}
              onChange={(e) => setChosenValue(Number(e.target.value))}
              aria-valuemin={minOverbid}
              aria-valuemax={maxOverbid}
              aria-valuenow={chosenValue}
            />
            <span className="bidding-panel-value-readout">
              {chosenValue} / {view.rc.totalTricks}
            </span>
          </div>

          <div
            className="bidding-panel-row bidding-panel-trumps"
            role="radiogroup"
            aria-label="Trump suit"
          >
            {SUITS.map((s) => {
              const selected = chosenTrump === s;
              return (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={`Trump ${SUIT_NAME[s]}`}
                  disabled={locked}
                  className={
                    "bidding-panel-trump" +
                    ` bidding-panel-trump-${s.toLowerCase()}` +
                    (selected ? " bidding-panel-trump-selected" : "")
                  }
                  onClick={() => setChosenTrump(s)}
                >
                  <span className="bidding-panel-trump-glyph" aria-hidden="true">
                    {SUIT_GLYPH[s]}
                  </span>
                  <span className="bidding-panel-trump-letter" aria-hidden="true">
                    {s}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="bidding-panel-actions">
            <button
              type="button"
              className="bidding-panel-pass"
              disabled={locked}
              onClick={onSubmitPass}
              aria-label="Pass this auction"
            >
              Pass
            </button>
            <button
              type="submit"
              className="bidding-panel-submit"
              disabled={locked}
              aria-label={`Raise to ${chosenValue} ${SUIT_NAME[chosenTrump]}`}
            >
              Raise to {chosenValue}{" "}
              <span aria-hidden="true">{SUIT_GLYPH[chosenTrump]}</span>
            </button>
          </div>
        </form>
      ) : (
        // Ceiling case: no legal raise. The engine still allows pass (it
        // resolves the auction synchronously when every non-high-bidder
        // has passed), so render a standalone Pass action.
        <div>
          <p className="bidding-panel-sub">
            The auction is at the ceiling — no further raises are legal.
          </p>
          <div className="bidding-panel-actions">
            <button
              type="button"
              className="bidding-panel-pass"
              disabled={locked}
              onClick={onSubmitPass}
              aria-label="Pass this auction"
            >
              Pass
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
